import { useDispatch, useSelector } from "react-redux";

import type {
    BaseActivatedPanelProps,
    SelectionDropdownItem,
} from "app/pages/sales-intelligence/common-components/table-actions-panel";
import { ManualSelectionItem } from "app/pages/sales-intelligence/common-components/table-actions-panel";
import { appsExportSelectionDropdown } from "app/pages/sales-intelligence/modules/apps-search/configuration/export/appsExportSelectionDropdown";
import { ExportCostTypeItem } from "app/pages/sales-intelligence/modules/quota/enums";
import { useExportQuota } from "app/pages/sales-intelligence/modules/quota/hooks/useExportQuota";
import { useLeadsExportCost } from "app/pages/sales-intelligence/modules/quota/hooks/useLeadsExportCost";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import trackingService from "app/pages/sales-intelligence/services/tracking/createMultiSelectorPanelService";
import LegacyWebsitesExcelExportPanelView from "app/pages/sales-intelligence/sub-modules/company-search/components/LegacyWebsitesExcelExportPanel/LegacyWebsitesExcelExportPanelView";
import WebsitesExcelExportPanelView from "app/pages/sales-intelligence/sub-modules/company-search/components/WebsitesExcelExportPanel/WebsitesExcelExportPanelView";

import { setAppsIsRowSelectionEnabled, setAppsSelectedRows } from "../../store/action-creators";
import { selectAppsSelectedRows } from "../../store/selectors";
import type { AppsExportRequestDto } from "../../types/dto";
import { buildAppsExportData } from "../../utils/dto";

type Props = BaseActivatedPanelProps & {
    numberOfResults: number;
    isLoading: boolean;
    onExport: (dto: AppsExportRequestDto) => void;
};

const AppsExcelExportPanelContainer = ({ numberOfResults, isLoading, onDeactivate, onExport }: Props) => {
    const dispatch = useDispatch();

    const excelQuota = useExportQuota();
    const exportCostState = useLeadsExportCost({ exportType: ExportCostTypeItem.Excel });

    const selectedRows = useSelector(selectAppsSelectedRows);

    const clearAndCloseSelection = (): void => {
        dispatch(setAppsIsRowSelectionEnabled(false));
        dispatch(setAppsSelectedRows([]));
    };

    const handleDeactivate = (): void => {
        clearAndCloseSelection();
        onDeactivate();
    };

    const handleSelectionChange = (item: Readonly<SelectionDropdownItem>): void => {
        if (item.id === ManualSelectionItem.ID) {
            dispatch(setAppsIsRowSelectionEnabled(true));
        } else {
            clearAndCloseSelection();
        }
    };

    const handleSubmit = (item: Readonly<SelectionDropdownItem>): void => {
        trackingService.trackMultiSelectorExportClick("excel", excelQuota.data.remaining, item.alias, item.value);

        handleDeactivate();

        const selectedAppIds = selectedRows.map((row) => row.app_id).filter((id): id is string => !!id);

        onExport(buildAppsExportData(item, selectedAppIds));
    };

    if (salesSettingsHelper.isDataCreditsEnabled) {
        return (
            <WebsitesExcelExportPanelView
                isDisabled={isLoading}
                isLoading={isLoading}
                numberOfSelectedItems={selectedRows.length}
                numberOfRemainingExports={excelQuota.data.remaining}
                numberOfResults={numberOfResults}
                selectionDropdown={appsExportSelectionDropdown}
                onDeactivate={handleDeactivate}
                onSelectionChange={handleSelectionChange}
                exportCostState={exportCostState}
                onSubmit={handleSubmit}
            />
        );
    }

    return (
        <LegacyWebsitesExcelExportPanelView
            isDisabled={isLoading}
            isLoading={isLoading}
            numberOfSelectedItems={selectedRows.length}
            numberOfRemainingExports={excelQuota.data.remaining}
            numberOfResults={numberOfResults}
            selectionDropdown={appsExportSelectionDropdown}
            onDeactivate={handleDeactivate}
            onSelectionChange={handleSelectionChange}
            onSubmit={handleSubmit}
        />
    );
};

export default AppsExcelExportPanelContainer;
